import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import Spinner from "../components/Spinner";
import PurchaseConfirmDialog from "../components/PurchaseConfirmDialog";
import AuthImage from "../components/AuthImage";
import OneInOneOutDialog from "../components/OneInOneOutDialog";
import {isOneInOneOutEnabled} from "../service/preferencesCache";
import {ArrowRightIcon, CheckCheckIcon, ChevronDownIcon, LinkIcon, SparklesIcon} from "../components/icons";
import {PurchaseGuidance} from "../components/PurchaseSpecPanel";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface ShoppingItem {
    id: string;
    title: string;
    categoryId: string | null;
    categoryName: string | null;
    note: string | null;
    imageKey?: string | null;
    status: string;
    sourceRecKey?: string | null;
    /** Роль пробела, породившего пункт (feature purchase-guidance); null у ручных пунктов. */
    roleCode?: string | null;
    /** Руководство по выбору из спецификации покупки; additive-поле. */
    guidance?: PurchaseGuidance | null;
    deepLink: string | null;
    plannedPriceRub: number | null;
    actualPriceRub: number | null;
    createdItemId: string | null;
    marketplaceCode?: string | null;
    createdAt: string | null;
    purchasedAt: string | null;
}

interface Suggestion {
    categoryId: string;
    title: string;
}

interface ShoppingList {
    items: ShoppingItem[];
    suggestions: Suggestion[];
    plannedCount: number;
    purchasedCount: number;
}

const money = (n: number | null | undefined): string =>
    n == null ? '—' : `${Math.round(n).toLocaleString('ru-RU')} ₽`;

const PURCHASE_ANALYSIS_PREFIX = 'PURCHASE_ANALYSIS:';

/** Секции руководства по покупке в порядке чтения: почему → каким → как выбирать → чего избегать. */
const GUIDANCE_SECTIONS: ReadonlyArray<{key: keyof PurchaseGuidance; title: string}> = [
    {key: 'whyNow', title: 'Почему это стоит купить'},
    {key: 'mustHave', title: 'Каким должен быть предмет'},
    {key: 'checkpoints', title: 'На что смотреть при выборе'},
    {key: 'avoid', title: 'Избегать'},
];

const hasGuidance = (guidance?: PurchaseGuidance | null): boolean =>
    !!guidance && GUIDANCE_SECTIONS.some(section => (guidance[section.key] ?? []).length > 0);

/**
 * Руководство по выбору на пункте списка покупок (feature purchase-guidance, FR-6): свёрнуто
 * по умолчанию — список остаётся списком, но в магазине можно свериться, не возвращаясь в ленту
 * рекомендаций. Нативный {@code <details>} даёт доступность и клавиатуру без своего состояния.
 */
const ItemGuidance: React.FC<{guidance: PurchaseGuidance}> = ({guidance}) => (
    <details className="mt-1 group" data-testid="shopping-item-guidance">
        <summary className="inline-flex min-h-[44px] cursor-pointer list-none items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400">
            <ChevronDownIcon size={14} className="transition-transform group-open:rotate-180"/>
            Что искать
        </summary>
        <div className="mt-1 space-y-2">
            {GUIDANCE_SECTIONS.map(section => {
                const items = guidance[section.key] ?? [];
                if (items.length === 0) {
                    return null;
                }
                return (
                    <div key={section.key}>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{section.title}</p>
                        <ul className="mt-0.5 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {items.map(entry => (
                                <li key={entry} className="flex gap-1.5">
                                    <span aria-hidden="true">•</span>
                                    <span>{entry}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    </details>
);

/** id породившего анализа из sourceRecKey (`PURCHASE_ANALYSIS:<id>`), иначе null. */
const purchaseAnalysisId = (sourceRecKey?: string | null): string | null =>
    sourceRecKey?.startsWith(PURCHASE_ANALYSIS_PREFIX)
        ? sourceRecKey.slice(PURCHASE_ANALYSIS_PREFIX.length)
        : null;

const ShoppingPage: React.FC = () => {
    const [data, setData] = useState<ShoppingList | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirmItem, setConfirmItem] = useState<ShoppingItem | null>(null);
    const [purchased, setPurchased] = useState<{title: string; createdItemId: string | null} | null>(null);
    // «Одно пришло — одно ушло»: созданная покупкой вещь для диалога кандидатов на выбытие
    const [oneInOneOutItem, setOneInOneOutItem] = useState<{id: string; name: string; categoryId: string | null} | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ShoppingList>('/shopping');
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    const add = async (payload: { title: string; categoryId?: string | null }) => {
        if (!payload.title.trim()) return;
        setBusy(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            await api.post('/shopping', payload);
            setTitle('');
            await load();
        } catch (e) {
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    // «Вернуть» в план: созданная покупкой вещь остаётся в гардеробе (необратимое — только вручную).
    const returnToPlanned = async (item: ShoppingItem) => {
        setBusy(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            await api.put(`/shopping/${item.id}/status`, {status: 'PLANNED'});
            await load();
        } catch (e) {
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    /** Подтверждение из модалки: purchase создаёт вещь в гардеробе; ошибку ловит диалог. */
    const confirmPurchase = async (item: ShoppingItem, priceRub: number, photoBase64: string | null) => {
        const api = await createAxiosInstance();
        const res = await api.post<ShoppingItem>(`/shopping/${item.id}/purchase`, {
            priceRub,
            photoBase64: photoBase64 ?? undefined,
        });
        setConfirmItem(null);
        setPurchased({title: item.title, createdItemId: res.data.createdItemId});
        const createdItemId = res.data.createdItemId;
        if (createdItemId && await isOneInOneOutEnabled()) {
            setOneInOneOutItem({id: createdItemId, name: item.title, categoryId: item.categoryId});
        }
        await load();
    };

    const remove = async (item: ShoppingItem) => {
        setBusy(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            await api.delete(`/shopping/${item.id}`);
            await load();
        } catch (e) {
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <Spinner/>;
    }

    if (error && !data) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить список покупок</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    const list = data!;

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-2xl">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Список покупок</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    Что докупить, чтобы закрыть пробелы гардероба. Без оплаты — только план.
                </p>
            </div>

            {error && (
                <p className="text-rose-600 dark:text-rose-400" data-testid="shopping-error">
                    Не удалось выполнить действие. Попробуйте ещё раз.
                </p>
            )}

            {purchased && (
                <div
                    className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-800 dark:text-emerald-200"
                    data-testid="shopping-purchased-banner"
                >
                    <CheckCheckIcon size={18}/>
                    <span>Вещь добавлена в гардероб: «{purchased.title}»</span>
                    {purchased.createdItemId && (
                        <Link
                            to={`/clothing/${purchased.createdItemId}`}
                            className="inline-flex items-center gap-1 font-medium underline hover:no-underline"
                            data-testid="shopping-created-item-link"
                        >
                            Открыть карточку <ArrowRightIcon size={16}/>
                        </Link>
                    )}
                </div>
            )}

            <form
                className="flex gap-2"
                onSubmit={e => {
                    e.preventDefault();
                    add({title});
                }}
            >
                <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Что купить?"
                    aria-label="Что купить"
                    maxLength={255}
                    className="flex-1 min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                />
                <button
                    type="submit"
                    disabled={!title.trim() || busy}
                    className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                    Добавить
                </button>
            </form>

            {list.suggestions.length > 0 && (
                <section data-testid="shopping-suggestions">
                    <h2 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Закрыть пробелы гардероба
                    </h2>
                    <div className="flex flex-wrap gap-2">
                        {list.suggestions.map(s => (
                            <button
                                key={s.categoryId}
                                type="button"
                                onClick={() => add({title: s.title, categoryId: s.categoryId})}
                                disabled={busy}
                                className="min-h-[44px] px-3 rounded-full border border-dashed border-brand-400 text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-ink-800 disabled:opacity-50"
                            >
                                + {s.title}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <div className="flex flex-wrap gap-2 text-sm" data-testid="shopping-counts">
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200">
                    К покупке: {list.plannedCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                    Куплено: {list.purchasedCount}
                </span>
            </div>

            {list.items.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400" data-testid="shopping-empty">
                    Список пуст — добавьте, что планируете купить.
                </p>
            ) : (
                <ul className="space-y-2" data-testid="shopping-list">
                    {list.items.map(item => {
                        const isPurchased = item.status === 'PURCHASED';
                        const analysisId = purchaseAnalysisId(item.sourceRecKey);
                        return (
                            <li key={item.id}
                                className="rounded-xl border border-gray-200 dark:border-ink-700 p-3 flex items-center gap-3">
                                {item.imageKey && (
                                    <span className="shrink-0" data-testid="shopping-item-photo">
                                        <AuthImage
                                            imageKey={item.imageKey}
                                            alt={`Фото «${item.title}»`}
                                            variant="thumbnail"
                                            className="w-11 h-14 min-w-[44px] object-cover rounded-lg"
                                        />
                                    </span>
                                )}
                                <div className="min-w-0 flex-1">
                                    <span className={`font-medium ${isPurchased ? 'line-through text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                        {item.title}
                                    </span>
                                    {item.categoryName && (
                                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                            {item.categoryName}
                                        </span>
                                    )}
                                    {item.note && (
                                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2"
                                            data-testid="shopping-item-note">
                                            {item.note}
                                        </p>
                                    )}
                                    {hasGuidance(item.guidance) && <ItemGuidance guidance={item.guidance!}/>}
                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                                        {analysisId && (
                                            <Link
                                                to={`/purchase-analysis?id=${analysisId}`}
                                                className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-400 underline hover:no-underline"
                                                data-testid="shopping-analysis-link"
                                            >
                                                <SparklesIcon size={14}/> AI-анализ
                                            </Link>
                                        )}
                                        {isPurchased && item.actualPriceRub != null && (
                                            <span data-testid="shopping-actual-price">
                                                куплено за {money(item.actualPriceRub)}
                                            </span>
                                        )}
                                        {!isPurchased && item.plannedPriceRub != null && (
                                            <span data-testid="shopping-planned-price">
                                                план: {money(item.plannedPriceRub)}
                                            </span>
                                        )}
                                        {isPurchased && item.createdItemId && (
                                            <Link
                                                to={`/clothing/${item.createdItemId}`}
                                                className="text-brand-600 dark:text-brand-400 underline hover:no-underline"
                                                data-testid="shopping-item-card-link"
                                            >
                                                вещь в гардеробе
                                            </Link>
                                        )}
                                    </div>
                                </div>
                                {item.deepLink && !isPurchased && (
                                    <a
                                        href={item.deepLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Открыть «${item.title}» на маркетплейсе`}
                                        className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl border border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-500"
                                        data-testid="shopping-deeplink"
                                    >
                                        <LinkIcon size={18}/>
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => (isPurchased ? returnToPlanned(item) : setConfirmItem(item))}
                                    disabled={busy}
                                    className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-gray-700 dark:text-gray-200 hover:border-brand-500 disabled:opacity-50"
                                >
                                    {isPurchased ? 'Вернуть' : 'Куплено'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(item)}
                                    disabled={busy}
                                    aria-label={`Удалить «${item.title}»`}
                                    className="min-h-[44px] px-3 rounded-xl text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-ink-800 disabled:opacity-50"
                                >
                                    Удалить
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {confirmItem && (
                <PurchaseConfirmDialog
                    itemTitle={confirmItem.title}
                    plannedPriceRub={confirmItem.plannedPriceRub}
                    onConfirm={(priceRub, photoBase64) => confirmPurchase(confirmItem, priceRub, photoBase64)}
                    onClose={() => setConfirmItem(null)}
                />
            )}

            {oneInOneOutItem && (
                <OneInOneOutDialog
                    addedItemId={oneInOneOutItem.id}
                    addedItemName={oneInOneOutItem.name}
                    categoryId={oneInOneOutItem.categoryId}
                    onClose={() => setOneInOneOutItem(null)}
                />
            )}
        </div>
    );
};

export default ShoppingPage;
